import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { useToast } from '@shared/hooks/use-toast';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CourierFilesApiAdapter } from '../adapters';
import { AdvancedFilter, CourierFileFilters, ExportCourierFilesRequest } from '../models';

// Columnas por defecto para exportar
const DEFAULT_EXPORT_COLUMNS = [
  'id',
  'file_name',
  'file_date',
  'total_declared',
  'processed_orders_count',
  'payments_total',
  'currency',
  'courier.name',
  'status',
  'imported_at',
  'bank_collected_at',
];

export const useExportCourierFiles = () => {
  const [isExporting, setIsExporting] = useState(false);
  const { toast } = useToast();
  const { t } = useTranslation('codCourierFiles');

  const exportCourierFiles = async (
    filters: Partial<CourierFileFilters>,
    columns?: string[],
    sorting?: { column: string; direction: 'asc' | 'desc' },
  ) => {
    setIsExporting(true);
    try {
      const httpClient = createHttpClient(apiClientWH);
      const adapter = new CourierFilesApiAdapter(httpClient);

      // Construir advanced_filters dinámicamente
      const advancedFilters: AdvancedFilter[] = [];

      if (filters.status) {
        advancedFilters.push({
          column: 'status',
          operator: 'equals',
          value: filters.status,
        });
      }

      if (filters.courier_id) {
        advancedFilters.push({
          column: 'courier_id',
          operator: 'in',
          value: [String(filters.courier_id)],
        });
      }

      if (filters.date_from) {
        advancedFilters.push({
          column: 'file_date',
          operator: 'greater_equal',
          value: filters.date_from,
        });
      }

      if (filters.date_to) {
        advancedFilters.push({
          column: 'file_date',
          operator: 'less_equal',
          value: filters.date_to,
        });
      }

      if (filters.search) {
        advancedFilters.push({
          column: 'file_name',
          operator: 'contains',
          value: filters.search,
        });
      }

      // Construir request
      const request: ExportCourierFilesRequest = {
        columns: columns || DEFAULT_EXPORT_COLUMNS,
        ...(advancedFilters.length > 0 && {
          advanced_filters: {
            filters: advancedFilters,
            logic: 'AND',
          },
        }),
      };

      const response = await adapter.exportCourierFiles(request, sorting);

      if (response.url_file) {
        // Abrir el archivo Excel en una nueva pestaña
        window.open(response.url_file, '_blank');

        toast({
          title: t('export.success.title', 'Exportación exitosa'),
          description: t('export.success.description', 'El archivo Excel se está descargando'),
          variant: 'default',
        });
      } else {
        throw new Error(t('export.error.no_data', 'No se encontraron archivos para exportar'));
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.error || error.message;
      toast({
        title: t('export.error.title', 'Error al exportar'),
        description: errorMessage || t('export.error.unexpected', 'Ocurrió un error inesperado'),
        variant: 'destructive',
      });
    } finally {
      setIsExporting(false);
    }
  };

  return {
    exportCourierFiles,
    isExporting,
  };
};
